import {
  FloorTagged,
  VolumeGrid,
  VolumeGridLike,
  VolumeHeightSample,
  VolumeLeafCallback,
} from './VolumeGrid';
import { TileArchiveCache } from './TileArchive';
import {
  TileParseWorkerClient,
  reconstructVolumeGrid,
} from '../workers/TileParseWorkerClient';

/**
 * Heightmap fallback signature. Returns the smooth analytic ground at (x, z)
 * along with a slope-aware normal so terrain-substituted floors don't flatten
 * to `[0, 1, 0]` for IK/projectile bounce. Implementations typically delegate
 * to `TiledTerrainStreamer.sampleHeightWithNormal`.
 */
export type TerrainFallback = (x: number, z: number) => VolumeHeightSample | null;

/**
 * Camera-driven tiled volume grid. Loads a 3x3 ring of tiles around the
 * camera's current tile position. Tiles outside the ring are evicted.
 * Queries against unloaded tiles return null (gracefully handled by consumers).
 *
 * Pulls volgrid bytes out of the shared `.tile` archive (one fetch per tile
 * shared with the navmesh streamer); skips fetches entirely for tiles that
 * the manifest's `volgridTiles` list doesn't advertise.
 */
export class TiledVolumeGrid implements VolumeGridLike {
  public voxelSize: number;
  private tilesX: number;
  private tilesZ: number;
  private tileWorldSize: number;
  private worldMinX: number;
  private worldMinZ: number;

  /** Returns true when this tile carries a volgrid payload inside its `.tile` archive. */
  private readonly hasVolgridFn: (tx: number, tz: number) => boolean;

  /** Loaded tile grids keyed by "tx,tz". */
  private loaded = new Map<string, VolumeGrid>();
  /** Tiles currently being fetched (prevents duplicate loads). */
  private loading = new Set<string>();

  private lastCameraTileX = -999;
  private lastCameraTileZ = -999;

  private archiveCache: TileArchiveCache;
  private workerClient: TileParseWorkerClient | undefined;

  /**
   * Called when a tile grid has no column data at (x, z) — either the whole tile
   * isn't loaded (current behaviour) OR a v3 volgrid omitted the column because
   * it's pure terrain. Also called for tagged-terrain bottom spans inside mixed
   * columns so the smooth heightmap Y replaces the int16-quantized volgrid Y.
   */
  private terrainFallback: TerrainFallback | null = null;

  constructor(
    tilesX: number,
    tilesZ: number,
    tileWorldSize: number,
    worldMinX: number,
    worldMinZ: number,
    voxelSize: number,
    hasVolgridFn: (tx: number, tz: number) => boolean,
    archiveCache: TileArchiveCache,
    workerClient?: TileParseWorkerClient,
  ) {
    this.tilesX = tilesX;
    this.tilesZ = tilesZ;
    this.tileWorldSize = tileWorldSize;
    this.worldMinX = worldMinX;
    this.worldMinZ = worldMinZ;
    this.voxelSize = voxelSize;
    this.hasVolgridFn = hasVolgridFn;
    this.archiveCache = archiveCache;
    this.workerClient = workerClient;
  }

  /**
   * Register a heightmap fallback. Called for any (x, z) where the volgrid has
   * no column data (whole tile missing, or pure-terrain column omitted from a
   * v3 volgrid) and for tagged-terrain bottom spans inside mixed columns.
   */
  setTerrainFallback(fn: TerrainFallback | null): void {
    this.terrainFallback = fn;
  }

  /** Returns true when a real volgrid tile is loaded for the tile covering (x, z). */
  hasTileAt(x: number, z: number): boolean {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    return this.loaded.has(`${tx},${tz}`);
  }

  /** Call each frame with camera world position to trigger tile loading/eviction. */
  updateCamera(x: number, z: number): void {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);

    if (tx === this.lastCameraTileX && tz === this.lastCameraTileZ) return;
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    // Determine which tiles should be loaded (3x3 ring)
    const needed = new Set<string>();
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          needed.add(`${ntx},${ntz}`);
        }
      }
    }

    // Evict tiles outside the ring
    for (const key of this.loaded.keys()) {
      if (!needed.has(key)) {
        this.loaded.delete(key);
      }
    }

    // Load missing tiles
    for (const key of needed) {
      if (!this.loaded.has(key) && !this.loading.has(key)) {
        this.loadTile(key);
      }
    }
  }

  /** Eagerly load a specific tile (used during scene init for the starting position). */
  async loadTileAt(tx: number, tz: number): Promise<void> {
    const key = `${tx},${tz}`;
    if (this.loaded.has(key) || this.loading.has(key)) return;
    await this.loadTile(key);
  }

  /** Load initial tiles around a starting position and wait for them to complete. */
  async loadInitialTiles(x: number, z: number): Promise<void> {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    this.lastCameraTileX = tx;
    this.lastCameraTileZ = tz;

    const promises: Promise<void>[] = [];
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const ntx = tx + dx;
        const ntz = tz + dz;
        if (ntx >= 0 && ntx < this.tilesX && ntz >= 0 && ntz < this.tilesZ) {
          promises.push(this.loadTileAt(ntx, ntz));
        }
      }
    }
    await Promise.all(promises);
  }

  private async loadTile(key: string): Promise<void> {
    this.loading.add(key);
    const [txStr, tzStr] = key.split(',');
    const tx = parseInt(txStr);
    const tz = parseInt(tzStr);

    if (!this.hasVolgridFn(tx, tz)) {
      // No volgrid payload for this tile — heightmap fallback covers floor queries.
      // The .tile archive is still fetched once by the navmesh streamer; we skip it
      // here entirely so we don't double-touch the cache for tiles that won't yield
      // a grid.
      this.loading.delete(key);
      return;
    }

    try {
      const archive = await this.archiveCache.get(tx, tz);
      const volBytes = archive?.volgrid;
      if (!volBytes) {
        return;
      }
      const buffer = volBytes.byteOffset === 0 && volBytes.byteLength === volBytes.buffer.byteLength
        ? (volBytes.buffer as ArrayBuffer)
        : volBytes.slice().buffer;

      let grid: VolumeGrid;
      if (this.workerClient) {
        // Transfer buffer to worker; scan pass runs off main thread.
        // The buffer is returned zero-copy alongside the branch-children encoding.
        const result = await this.workerClient.parseVolgrid(buffer);
        grid = reconstructVolumeGrid(result);
      } else {
        grid = VolumeGrid.read(buffer);
      }

      this.loaded.set(key, grid);
    } catch (e) {
      console.warn(`Failed to load volgrid tile (${tx},${tz}):`, e);
    } finally {
      this.loading.delete(key);
    }
  }

  private getTileGrid(x: number, z: number): VolumeGrid | undefined {
    const tx = Math.floor((x - this.worldMinX) / this.tileWorldSize);
    const tz = Math.floor((z - this.worldMinZ) / this.tileWorldSize);
    return this.loaded.get(`${tx},${tz}`);
  }

  // --- VolumeGridLike implementation: delegate to the correct tile ---

  isSolidAt(x: number, y: number, z: number): boolean {
    return this.getTileGrid(x, z)?.isSolidAt(x, y, z) ?? false;
  }

  /**
   * Apply the terrain fallback to a tile result. Returns the substituted Y if:
   *  - tile result is null (no column at this XZ — pure-terrain or no tile), OR
   *  - tile result is tagged terrain-origin and the fallback supplied a Y.
   * Otherwise returns the volgrid Y.
   */
  private resolveFloorY(tile: FloorTagged | null | undefined, x: number, z: number): number | null {
    if (tile == null) return this.terrainFallback?.(x, z)?.height ?? null;
    if (!tile.terrainOrigin) return tile.y;
    const fb = this.terrainFallback?.(x, z);
    return fb?.height ?? tile.y;
  }

  getFloorHeight(x: number, y: number, z: number): number | null {
    const tile = this.getTileGrid(x, z)?.getFloorHeightTagged(x, y, z);
    return this.resolveFloorY(tile, x, z);
  }

  getFloorHeightTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getTileGrid(x, z)?.getFloorHeightTagged(x, y, z) ?? null;
  }

  getCeilingHeight(x: number, y: number, z: number): number | null {
    return this.getTileGrid(x, z)?.getCeilingHeight(x, y, z) ?? null;
  }

  getCeilingHeightAbove(x: number, y: number, z: number): number | null {
    return this.getTileGrid(x, z)?.getCeilingHeightAbove(x, y, z) ?? null;
  }

  getFloorHeightBelow(x: number, y: number, z: number): number | null {
    const tile = this.getTileGrid(x, z)?.getFloorHeightBelowTagged(x, y, z);
    return this.resolveFloorY(tile, x, z);
  }

  getFloorHeightBelowTagged(x: number, y: number, z: number): FloorTagged | null {
    return this.getTileGrid(x, z)?.getFloorHeightBelowTagged(x, y, z) ?? null;
  }

  /**
   * Smoothed floor query. The single-tile bilinear path mixes volgrid and
   * fallback Y at column boundaries (one cell of kink). To stay continuous
   * across mixed/terrain seams, short-circuit to the fallback's bilinear when
   * any of the 4 neighbours would either fall back OR carry the terrain tag.
   */
  getFloorHeightBelowSmooth(x: number, y: number, z: number): number | null {
    const grid = this.getTileGrid(x, z);
    if (!grid) {
      return this.terrainFallback?.(x, z)?.height ?? null;
    }
    if (this.shouldUseFallbackForSmoothFloor(grid, x, y, z)) {
      const fb = this.terrainFallback?.(x, z);
      if (fb !== null && fb !== undefined) return fb.height;
    }
    return grid.getFloorHeightBelowSmooth(x, y, z);
  }

  private shouldUseFallbackForSmoothFloor(grid: VolumeGrid, x: number, y: number, z: number): boolean {
    if (this.terrainFallback === null) return false;
    const vs = this.voxelSize;
    // Same 4-neighbour pattern getFloorHeightBelowSmooth would hit on the single tile.
    const samples: Array<[number, number]> = [
      [x, z],
      [x + vs, z],
      [x, z + vs],
      [x + vs, z + vs],
    ];
    for (const [sx, sz] of samples) {
      const r = grid.getFloorHeightBelowTagged(sx, y, sz);
      if (r === null || r.terrainOrigin) return true;
    }
    return false;
  }

  getFloorHeightWithNormal(x: number, y: number, z: number): VolumeHeightSample | null {
    const grid = this.getTileGrid(x, z);
    const tagged = grid?.getFloorHeightTagged(x, y, z);
    if (tagged && !tagged.terrainOrigin) {
      return grid!.getFloorHeightWithNormal(x, y, z);
    }
    const fb = this.terrainFallback?.(x, z);
    if (fb !== null && fb !== undefined) return fb;
    if (tagged) {
      // Tagged terrain but no fallback available — fall back to the volgrid normal.
      return grid!.getFloorHeightWithNormal(x, y, z);
    }
    return null;
  }

  forEachLeaf(callback: VolumeLeafCallback): void {
    for (const grid of this.loaded.values()) {
      grid.forEachLeaf(callback);
    }
  }

  rayCrossesSolid(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
    // Ray might cross tile boundaries — check both endpoints' tiles
    const gridA = this.getTileGrid(x0, z0);
    const gridB = this.getTileGrid(x1, z1);

    if (gridA && gridA === gridB) {
      return gridA.rayCrossesSolid(x0, y0, z0, x1, y1, z1);
    }

    // Fallback: step-based check that handles tile crossings
    const dx = x1 - x0;
    const dy = y1 - y0;
    const dz = z1 - z0;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (dist < 0.001) return this.isSolidAt(x1, y1, z1);

    const steps = Math.ceil(dist / this.voxelSize);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (this.isSolidAt(x0 + dx * t, y0 + dy * t, z0 + dz * t)) {
        return true;
      }
    }
    return false;
  }
}

